/* ============================================================================
   LX QUICKSHOP — design-led quick-shop for L'EXUDE cards.
   ----------------------------------------------------------------------------
   Same architecture as Dawn's quick-add: on open we fetch the product page
   (locale-aware), parse the pieces the lx theme already renders there —
   [data-lx-product-data] JSON, .lx-gal media, the mediablazer buybox strip
   ("FROM THE FEED" mirrors it 1:1 by construction), the Judge.me legacy
   review HTML — and compose them into a designed native <dialog>.

   Wiring: card buttons carry data-lx-quickshop (see lx-product-card.liquid).
   We intercept their clicks in CAPTURE phase, so Dawn's modal-opener (still
   attached, still functional) never fires while this file is alive. Kill
   this script and the untouched Dawn quick-add modal takes over — fallback
   preserved by design.

   ATC: a real Dawn <product-form> (product-form.js is loaded by the mount
   snippet) -> POST routes.cart_add_url with drawer sections -> cart drawer
   renderContents/open. We close ourselves on the resulting cartUpdate.

   RTL: geometry uses rect deltas + scrollBy (direction-agnostic); CSS uses
   logical properties. Eastern digits arrive free via lx-ar-digits' observer
   (it skips .money spans — money markup is preserved on purpose).

   v2 (owner feedback batch):
   - FROM-THE-FEED items join the MAIN rail after the product media (dots
     include them; the strip below became quick-jump shortcuts). Feed videos
     render poster+play slides -> inline overlay player, as before.
   - photo slides open the shared LxLightbox (images only — videos excluded
     from lightbox nav by design) with the magnifier hover chip.
   - reviews = compact single-card carousel (dots + hover arrows + swipe,
     manual only, max 4).
   - sold-out sizes render EXACTLY like the product page (no strike/gray —
     still clickable); selecting one swaps ATC for a NOTIFY-ME deep link to
     the product page (?variant= preselects it where the Peppy back-in-stock
     widget lives — its trigger is product-page-only, not globally callable).

   v3 (owner feedback batch):
   - the desktop dialog is bigger on BOTH axes (1220px wide, 720px height
     floor so sparse products stop rendering stubby). CSS-only — see the panel
     block in lx-quickshop.css. The <=1024px bottom sheet is untouched.
   - gallery dots left the stage: they are now a sibling BELOW it, and they
     share one rule block with the TOP REVIEWS dots, so the two carousels are
     literally the same component (6px round, ink active, grey inactive). They
     still span the unified flow (product media + FROM THE FEED items).
   - low-stock urgency chip moved ABOVE the "Size: EU|US|UK" row, mirroring
     the product page. Same key, same trigger: after ANY size click (owner
     call), re-animating on every click including re-clicks.

   v4 (owner feedback batch):
   - FROM-THE-FEED strip retired: feed items now CONTINUE the thumbnail rail
     after the product media (unified stage order), as one wrapping grid of
     larger (76px) square thumbs. Feed video thumbs carry a play badge; any
     thumb drives the stage (video -> jump to its poster+play slide as before).
   - NO PRE-SELECTED SIZE for products with options: they open with nothing
     chosen. Pressing ATC with no size prompts "select a size" (guarded in the
     submit capture phase — never POSTs a wrong/empty variant) and flashes the
     pills. Single-variant products still mount their only variant.
   - reviews AUTOPLAY: auto-advance every 3s, looping; paused on hover/focus/
     drag; manual dots/arrows reset the clock; prefers-reduced-motion = off.
   - arrow-select/darken bug fixed (see wireShell + .lx-qs__stage CSS).
   - dialog widened to 1300px; the "View full details" text link became a
     ghost-pill BUTTON anchored bottom-right of the info column.

   v5 (info-column revamp): visual work lives in lx-quickshop.css; this file
   only gained two markup hooks — the trust row's middot separators became
   three .lx-qs__trust-item spans with an inline stroke-tick SVG each, and
   review cards lead with a .lx-qs__review-avatar chip (reviewer's first
   initial; stars moved after the name — CSS anchors them inline-end). No
   behavior changed: dots/autoplay/swipe, drag, lightbox, ATC/notify flows
   and the .money markup are byte-identical.
   ========================================================================= */
(function () {
  'use strict';
  if (window.__lxQuickshopLoaded) return;
  window.__lxQuickshopLoaded = true;

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  /* ---- lx-notify lazy loader --------------------------------------------
     Build A: sold-out sizes now open the shared LxNotify modal instead of
     deep-linking to the product page. The notify snippet is only rendered on
     the product page, so on card pages we inject the engine ourselves. We
     derive its CDN asset URL (hash + version query intact) from THIS script's
     own <src>, so no change to snippets/lx-quickshop.liquid is required.
     Called on mount() so the modal is ready by the time a sold-out size is
     picked; window.LxNotify carries its own load-once guard. If the inject
     ever fails, the notify anchor's href deep-link stays as the fallback. */
  function ensureNotifyLoaded() {
    if (window.LxNotify || window.__lxNotifyLoaded) return;
    var self =
      document.currentScript ||
      document.querySelector('script[src*="lx-quickshop.js"]');
    var base = self && self.src;
    if (!base || base.indexOf('lx-quickshop.js') === -1) return;
    var cssHref = base.replace('lx-quickshop.js', 'lx-notify.css');
    var jsSrc = base.replace('lx-quickshop.js', 'lx-notify.js');
    if (!document.querySelector('link[href*="lx-notify.css"]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      document.head.appendChild(link);
    }
    if (!document.querySelector('script[src*="lx-notify.js"]')) {
      var s = document.createElement('script');
      s.src = jsSrc;
      s.defer = true;
      document.head.appendChild(s);
    }
  }

  /* ---- config (first mount blob wins) ----------------------------------- */
  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s;
    var box = document.createElement('textarea');
    box.innerHTML = s;
    return box.value;
  }

  function readConfig() {
    var el = document.querySelector('[data-lx-qs-config]');
    var cfg = { i18n: {}, root: '/' };
    if (el) {
      try {
        cfg = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[lx-quickshop] bad config JSON', e);
      }
    }
    cfg.i18n = cfg.i18n || {};
    // Shopify's `t` filter HTML-escapes non-_html translation values; decode
    // once here — everything is re-escaped exactly once at insertion (esc()).
    Object.keys(cfg.i18n).forEach(function (k) {
      if (typeof cfg.i18n[k] === 'string') cfg.i18n[k] = decodeEntities(cfg.i18n[k]);
    });
    return cfg;
  }

  /* locale root: '' for EN, '/ar' on the Arabic locale — derived from the
     always-present window.routes so it works on every template. */
  function localeRoot() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return cartUrl.replace(/\/cart\/?$/, '');
  }

  function localizeUrl(url) {
    if (!url) return url;
    var path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    var root = localeRoot();
    if (root && path.indexOf(root + '/') !== 0 && path !== root) path = root + path;
    return path;
  }

  var CFG = null;
  var T = null; // i18n shortcut
  var cache = {}; // handle -> model
  var pending = {}; // handle -> promise
  var dlg = null;
  var els = {};
  var state = null;

  var SVG = {
    close:
      '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1.5 1.5l11 11M12.5 1.5l-11 11"/></svg>',
    chevron:
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>',
    play: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 4.8c0-1.4 1.5-2.2 2.7-1.6l11 6.6c1.1.7 1.1 2.4 0 3.1l-11 6.6c-1.2.7-2.7-.2-2.7-1.6V4.8Z"/></svg>',
    star: '<svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="m234.29 114.85-45 38.83L203 211.75a16.4 16.4 0 0 1-24.5 17.82L128 198.49l-50.53 31.08A16.4 16.4 0 0 1 53 211.75l13.76-58.07-45-38.83A16.46 16.46 0 0 1 31.08 86l59-4.76 22.76-55.08a16.36 16.36 0 0 1 30.27 0l22.75 55.08 59 4.76a16.46 16.46 0 0 1 9.37 28.86Z"/></svg>',
    cart: '<svg viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M230.14,58.87A8,8,0,0,0,224,56H62.68L56.6,22.57A8,8,0,0,0,48.73,16H24a8,8,0,0,0,0,16h18L67.56,172.29a24,24,0,0,0,5.33,11.27,28,28,0,1,0,44.4,8.44h45.42A27.75,27.75,0,0,0,160,204a28,28,0,1,0,28-28H91.17a8,8,0,0,1-7.87-6.57L80.13,152h116a24,24,0,0,0,23.61-19.71l12.16-66.86A8,8,0,0,0,230.14,58.87ZM104,204a12,12,0,1,1-12-12A12,12,0,0,1,104,204Zm96,0a12,12,0,1,1-12-12A12,12,0,0,1,200,204Z"/></svg>',
    arrow:
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.5 8h11M9 3.5 13.5 8 9 12.5"/></svg>',
    ruler:
      '<svg viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M238.15,70.54,185.46,17.86a20,20,0,0,0-28.29,0L17.85,157.17a20,20,0,0,0,0,28.29l52.69,52.68a20,20,0,0,0,28.29,0L238.15,98.83A20,20,0,0,0,238.15,70.54ZM84.68,218.34l-47-47L64,145l23.52,23.52a12,12,0,0,0,17-17L81,128l15-15,23.51,23.52a12,12,0,0,0,17-17L113,96l15-15,23.52,23.52a12,12,0,0,0,17-17L145,64l26.35-26.34,47,47Z"/></svg>',
    bell: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M18 8.4c0-3.2-2.7-5.9-6-5.9S6 5.2 6 8.4c0 6.9-3 8.1-3 8.1h18s-3-1.2-3-8.1Z"/><path d="M13.7 20.2a2 2 0 0 1-3.4 0"/></svg>',
    tick: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M5.2 8.3l1.9 1.9 3.7-4.1"/></svg>',
    spinner:
      '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>'
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function stars(score) {
    var out = '';
    for (var i = 1; i <= 5; i++) out += i <= score ? SVG.star : SVG.star.replace('<svg ', '<svg data-off ');
    return out;
  }

  /* Shopify.formatMoney port (same as lx-product.js) — money_format embeds
     <span class=money>, which Doubly Global Currency + the /ar western-digit
     guard both rely on. Keep it byte-compatible. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var d = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var c = parts[1] ? decimal + parts[1] : '';
      return d + c;
    }
    var value = '';
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = fmt(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = fmt(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = fmt(cents, 0, '.', ',');
        break;
      default:
        value = fmt(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function reconvertCurrency() {
    if (window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === 'function') {
      setTimeout(function () {
        window.DoublyGlobalCurrency.convertAll();
      }, 0);
    }
  }

  /* ======================================================================== */
  /* Fetch + parse                                                            */
  /* ======================================================================== */

  function load(handle, url) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (pending[handle]) return pending[handle];
    pending[handle] = fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var model = parseDoc(doc, url);
        cache[handle] = model;
        delete pending[handle];
        return model;
      })
      .catch(function (err) {
        delete pending[handle];
        throw err;
      });
    return pending[handle];
  }

  function parseDoc(doc, url) {
    var dataEl = doc.querySelector('[data-lx-product-data]');
    if (!dataEl) throw new Error('lx product data missing');
    var data = JSON.parse(dataEl.textContent);

    // title without the SEO visually-hidden descriptor
    var title = '';
    var titleEl = doc.querySelector('.lx-mp__title');
    if (titleEl) {
      var tc = titleEl.cloneNode(true);
      tc.querySelectorAll('.visually-hidden').forEach(function (n) {
        n.remove();
      });
      title = tc.textContent.replace(/\s+/g, ' ').trim();
    }
    if (!title) {
      var og = doc.querySelector('meta[property="og:title"]');
      title = (og && og.getAttribute('content')) || data.handle || '';
    }

    var badgeEl = doc.querySelector('.lx-mp__titlerow .lx-badge');
    var countEl = doc.querySelector('.lx-mp__review-count');
    var priceEl = doc.querySelector('[data-lx-price]');
    var compareEl = doc.querySelector('[data-lx-compare]');
    var fourpayMsgEl = doc.querySelector('.lx-mp__fourpay-msg');
    var fourpayIcons = [];
    doc.querySelectorAll('.lx-mp__fourpay-iconrow img').forEach(function (img) {
      fourpayIcons.push({ src: img.getAttribute('src') || '', alt: img.getAttribute('alt') || '' });
    });

    // main gallery media (lx-gal slides, in product.media order)
    var media = [];
    doc.querySelectorAll('.lx-gal [data-lx-gal-slide]').forEach(function (slide) {
      var video = slide.querySelector('video');
      var iframe = slide.querySelector('iframe');
      var img = slide.querySelector('img');
      if (video) {
        media.push({
          type: 'video',
          src: video.getAttribute('data-src') || video.getAttribute('src') || '',
          poster: video.getAttribute('poster') || '',
          w: parseInt(video.getAttribute('width'), 10) || 0,
          h: parseInt(video.getAttribute('height'), 10) || 0,
          alt: ''
        });
      } else if (iframe) {
        media.push({ type: 'embed', src: iframe.getAttribute('src') || '', poster: '', alt: iframe.getAttribute('title') || '' });
      } else if (img) {
        media.push({
          type: 'image',
          src: img.getAttribute('src') || '',
          srcset: img.getAttribute('srcset') || '',
          alt: img.getAttribute('alt') || '',
          w: parseInt(img.getAttribute('width'), 10) || 0,
          h: parseInt(img.getAttribute('height'), 10) || 0
        });
      }
    });

    // FROM THE FEED — direct mirror of the buybox mediablazer strip
    var feed = [];
    doc.querySelectorAll('.mediablazer [data-mb-tile]').forEach(function (tile) {
      var v = tile.querySelector('video');
      var item = {
        kind: tile.getAttribute('data-mb-kind') || 'image',
        video: tile.getAttribute('data-mb-video') || '',
        image: tile.getAttribute('data-mb-image') || '',
        poster: v ? v.getAttribute('poster') || '' : ''
      };
      if (item.video || item.image || item.poster) feed.push(item);
    });

    // Judge.me legacy reviews (server-rendered metafield HTML)
    var reviews = [];
    doc.querySelectorAll('.jdgm-legacy-widget-content .jdgm-rev').forEach(function (rev) {
      var bodyEl = rev.querySelector('.jdgm-rev__body');
      var text = bodyEl ? bodyEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text) return;
      var ratingEl = rev.querySelector('.jdgm-rev__rating');
      var score = ratingEl ? parseInt(ratingEl.getAttribute('data-score'), 10) : 5;
      if (isNaN(score) || score < 1 || score > 5) score = 5;
      var authorEl = rev.querySelector('.jdgm-rev__author');
      var author = authorEl ? authorEl.textContent.replace(/\s+/g, ' ').trim() : '';
      reviews.push({ score: score, author: author, text: text });
    });
    reviews.sort(function (a, b) {
      return b.score - a.score;
    });
    reviews = reviews.slice(0, 4);

    var variants = data.variants || [];
    var hasOptions = variants.length > 1 || (variants[0] && variants[0].option1 && variants[0].option1 !== 'Default Title');

    var idInput = doc.querySelector('[data-lx-variant-id]');
    var initialId = idInput ? idInput.getAttribute('value') : null;
    var initial = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(initialId)) initial = variants[i];
    }
    if (!initial) initial = variants[0] || null;

    return {
      data: data,
      title: title,
      badgeHtml: badgeEl ? badgeEl.outerHTML : '',
      reviewCount: countEl ? countEl.textContent.trim() : '',
      priceHtml: priceEl ? priceEl.innerHTML : '',
      compareHtml: compareEl ? compareEl.innerHTML : '',
      compareHidden: compareEl ? compareEl.getAttribute('data-hidden') === 'true' : true,
      fourpayMsgHtml: fourpayMsgEl ? fourpayMsgEl.innerHTML : '',
      fourpayIcons: fourpayIcons,
      media: media,
      feed: feed,
      reviews: reviews,
      hasOptions: hasOptions,
      initial: initial,
      url: url
    };
  }

  /* ======================================================================== */
  /* Shell                                                                    */
  /* ======================================================================== */

  function ensureShell() {
    if (dlg) return;
    dlg = document.createElement('dialog');
    dlg.className = 'lx-qs';
    dlg.innerHTML =
      '<div class="lx-qs__panel" tabindex="-1" data-qs-panel>' +
      '<button type="button" class="lx-qs__grip" data-qs-grip aria-hidden="true" tabindex="-1"><span class="lx-qs__grip-pill"></span></button>' +
      '<button type="button" class="lx-qs__close" data-qs-close aria-label="' + esc(T.close) + '">' + SVG.close + '</button>' +
      '<div class="lx-qs__scroll" data-qs-scroll>' +
      '<div class="lx-qs__media" data-qs-mediacol>' +
      '<div class="lx-qs__stage" data-qs-stage>' +
      '<div class="lx-qs__rail" data-qs-rail></div>' +
      '<button type="button" class="lx-qs__nav lx-qs__nav--prev" data-qs-prev aria-label="' + esc(T.prevSlide) + '">' +
      SVG.chevron + '</button>' +
      '<button type="button" class="lx-qs__nav lx-qs__nav--next" data-qs-next aria-label="' + esc(T.nextSlide) + '">' + SVG.chevron + '</button>' +
      '</div>' +
      // v3: dots are a SIBLING of the stage, not a child. Inside it they were
      // an absolute frosted pill sitting on the photo AND were subject to the
      // stage's overflow:hidden. Below it they can neither overlap nor clip.
      '<div class="lx-qs__dots" data-qs-dots role="tablist" aria-label="' + esc(T.slideOf) + '"></div>' +
      // v4: the FROM-THE-FEED strip is gone. Feed items now continue the SAME
      // wrapping thumbnail rail after the product media (unified stage order),
      // so there is one grid of larger square thumbs, not a rail + a strip.
      '<div class="lx-qs__thumbs" data-qs-thumbs></div>' +
      '</div>' +
      '<div class="lx-qs__info" data-qs-infocol>' +
      '<div class="lx-qs__head">' +
      '<div class="lx-qs__titlerow"><h2 class="lx-qs__title" data-qs-title></h2><span data-qs-badge></span></div>' +
      '<div class="lx-qs__rating" data-qs-rating hidden>' +
      '<span class="lx-qs__stars" aria-hidden="true">' + stars(5) + '</span>' +
      '<span class="lx-qs__rating-count" data-qs-count></span>' +
      '<span class="lx-qs__rating-word">' + esc(T.reviewsWord) + '</span>' +
      '</div>' +
      '</div>' +
      '<div class="lx-qs__pricebox">' +
      '<div class="lx-qs__pricerow">' +
      '<span class="lx-qs__price" data-qs-price></span>' +
      '<span class="lx-qs__compare" data-qs-compare data-hidden="true"></span>' +
      '</div>' +
      '<div class="lx-qs__fourpay" data-qs-fourpay hidden>' +
      '<span data-qs-fourpay-msg></span>' +
      '<span class="lx-qs__fourpay-icons" data-qs-fourpay-icons></span>' +
      '</div>' +
      '</div>' +
      '<div class="lx-qs__sizes" data-qs-sizes hidden>' +
      // v3: low-stock chip LEADS the size block (mirrors the product page).
      // Same key, same trigger, same 200ms rise — only the position changed.
      '<div class="lx-qs__lowstock" data-qs-lowstock hidden aria-live="polite">' +
      '<span class="lx-qs__lowstock-dot" aria-hidden="true"></span>' +
      '<span class="lx-qs__lowstock-text">' + esc(T.lowStock) + '</span>' +
      '</div>' +
      '<div class="lx-qs__sizehead">' +
      '<span class="lx-qs__sizelabel">' + esc(T.sizeLabel) + '</span>' +
      '<span class="lx-qs__units" data-qs-units hidden></span>' +
      '<button type="button" class="lx-qs__sizeguide" data-qs-sizeguide>' + SVG.ruler + '<span>' + esc(T.sizeGuide) + '</span></button>' +
      '</div>' +
      '<fieldset class="lx-qs__pills" data-qs-pills></fieldset>' +
      '</div>' +
      '<div class="lx-qs__qty" data-qs-qty hidden>' +
      '<span class="lx-qs__qty-label">' + esc(T.qtyLabel) + '</span>' +
      '<span class="lx-qs__qty-box">' +
      '<button type="button" class="lx-qs__qty-btn" data-qs-qty-minus aria-label="&minus;">&minus;</button>' +
      '<input class="lx-qs__qty-input" data-qs-qty-input type="text" inputmode="numeric" autocomplete="off" value="1" aria-label="' + esc(T.qtyLabel) + '">' +
      '<button type="button" class="lx-qs__qty-btn" data-qs-qty-plus aria-label="+">+</button>' +
      '</span>' +
      '</div>' +
      '<product-form class="lx-qs__form" data-qs-productform>' +
      '<form method="post" action="' + esc(localeRoot() + '/cart/add') + '" data-type="add-to-cart-form" enctype="multipart/form-data" novalidate="novalidate">' +
      '<input type="hidden" name="id" value="" data-qs-id>' +
      '<input type="hidden" name="quantity" value="1" data-qs-quantity>' +
      '<button type="submit" name="add" class="lx-qs__atc" data-qs-atc>' +
      '<div class="loading__spinner hidden">' + SVG.spinner + '</div>' +
      SVG.cart +
      '<span data-qs-atc-text>' + esc(T.addToCart) + '</span>' +
      '</button>' +
      '<div class="product-form__error-message-wrapper lx-qs__error" role="alert" hidden>' +
      '<span class="product-form__error-message"></span>' +
      '</div>' +
      '</form>' +
      '</product-form>' +
      '<a class="lx-qs__notify" data-qs-notify href="#" hidden>' + SVG.bell + '<span>' + esc(T.notifyMe) + '</span></a>' +
      // v5: middots -> stroke-tick items (CSS colors the ticks brand orange)
      '<div class="lx-qs__trust">' +
      '<span class="lx-qs__trust-item">' + SVG.tick + '<span>' + esc(T.trustReturns) + '</span></span>' +
      '<span class="lx-qs__trust-item">' + SVG.tick + '<span>' + esc(T.trustShipping) + '</span></span>' +
      '<span class="lx-qs__trust-item">' + SVG.tick + '<span>' + esc(T.trustPaylater) + '</span></span>' +
      '</div>' +
      '<div class="lx-qs__reviews" data-qs-reviews hidden>' +
      '<h3 class="lx-qs__reviews-head">' + esc(T.topReviews) + '</h3>' +
      '<div class="lx-qs__reviews-vp" data-qs-revvp>' +
      '<div class="lx-qs__reviews-track" data-qs-revtrack></div>' +
      '<button type="button" class="lx-qs__rev-nav lx-qs__rev-nav--prev" data-qs-revprev aria-label="' + esc(T.prevSlide) + '">' +
      SVG.chevron + '</button>' +
      '<button type="button" class="lx-qs__rev-nav lx-qs__rev-nav--next" data-qs-revnext aria-label="' + esc(T.nextSlide) + '">' + SVG.chevron + '</button>' +
      '</div>' +
      '<div class="lx-qs__rev-dots" data-qs-revdots role="tablist" aria-label="' + esc(T.slideOf) + '"></div>' +
      '</div>' +
      // v4: a flex-grow spacer (desktop) pins the details BUTTON to the
      // bottom-right of the info column; it keeps a min gap when content
      // overflows, so the button never overlaps the reviews/ATC.
      '<div class="lx-qs__grow" aria-hidden="true"></div>' +
      '<a class="lx-qs__details" data-qs-details href="#"><span>' + esc(T.viewFullDetails) + '</span>' + SVG.arrow + '</a>' +
      '</div>' +
      '</div>' +
      '<div class="lx-qs__player" data-qs-player>' +
      '<button type="button" class="lx-qs__player-close" data-qs-player-close aria-label="' + esc(T.close) + '">' + SVG.close + '</button>' +
      '<video data-qs-player-video controls playsinline preload="metadata"></video>' +
      '</div>' +
      '</div>';
    document.body.appendChild(dlg);

    els = {};
    dlg.querySelectorAll('*').forEach(function (el) {
      for (var i = 0; i < el.attributes.length; i++) {
        var name = el.attributes[i].name;
        if (name.indexOf('data-qs-') === 0) {
          var key = name.slice(8).replace(/-([a-z])/g, function (m, c) {
            return c.toUpperCase();
          });
          els[key] = el;
        }
      }
    });

    wireShell();
  }

  function wireShell() {
    els.close.addEventListener('click', function () {
      hide();
    });
    dlg.addEventListener('cancel', function (e) {
      e.preventDefault();
      hide();
    });
    // backdrop close, drag-safe: only close when the pointerDOWN also landed on
    // the wash (dlg itself), so a drag that starts inside the panel — dragging
    // the gallery, selecting text — and ends on the wash never closes.
    var qsDownOnBackdrop = false;
    dlg.addEventListener(
      'pointerdown',
      function (e) {
        qsDownOnBackdrop = e.target === dlg;
      },
      true
    );
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg && qsDownOnBackdrop) hide();
      qsDownOnBackdrop = false;
    });

    // ARROW-SELECT / DARKEN FIX (recon item 4). Root cause: a rapid second
    // arrow click registers as a double-click, whose default action extends a
    // browser selection across the flex stage onto the underlying <img>; the
    // theme's global `::selection { background: rgba(ink,.2) }` (base.css)
    // then paints a dark wash over the photo. Guard: preventDefault on
    // pointer/mousedown so the click never seeds/extends a selection, plus
    // stopPropagation on the click (belt-and-suspenders). The stage itself
    // also carries user-select:none now (see lx-quickshop.css).
    function armNav(btn, delta) {
      btn.addEventListener('mousedown', function (e) {
        e.preventDefault();
      });
      btn.addEventListener('pointerdown', function (e) {
        e.preventDefault();
      });
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        goTo(activeIndex() + delta);
      });
    }
    armNav(els.prev, -1);
    armNav(els.next, 1);

    var scrollRaf = null;
    els.rail.addEventListener('scroll', function () {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        syncActive();
      });
    });

    // STAGE DRAG-SWIPE (desktop mouse/pen). Touch keeps the native scroll-snap
    // rail; a mouse can't drag a scroll container, so we follow scrollLeft by
    // hand (physical, so LTR/RTL are identical) and commit to prev/next on
    // release past a threshold, otherwise settle back. A committed drag arms
    // stageSwallow so the trailing click doesn't open the lightbox / a video.
    var stageDrag = null;
    var stageSwallow = false;
    els.stage.addEventListener(
      'click',
      function (e) {
        if (stageSwallow) {
          stageSwallow = false;
          e.stopPropagation();
          e.preventDefault();
        }
      },
      true
    );
    els.rail.addEventListener('pointerdown', function (e) {
      stageSwallow = false; // a fresh press voids any stale swallow
      if (e.pointerType === 'touch') return; // native rail owns touch
      if (e.target.closest('button, video')) return; // leave controls/play alone
      stageDrag = { id: e.pointerId, startX: e.clientX, startY: e.clientY, startScroll: els.rail.scrollLeft, moved: false };
    });
    els.rail.addEventListener('pointermove', function (e) {
      if (!stageDrag || stageDrag.id !== e.pointerId) return;
      var dx = e.clientX - stageDrag.startX;
      var dy = e.clientY - stageDrag.startY;
      if (!stageDrag.moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          stageDrag = null;
          return;
        }
        stageDrag.moved = true;
        els.rail.classList.add('is-dragging');
        try {
          els.rail.setPointerCapture(stageDrag.id);
        } catch (err) {
          /* noop */
        }
      }
      els.rail.scrollLeft = stageDrag.startScroll - dx;
      e.preventDefault();
    });
    function endStageDrag(e) {
      if (!stageDrag || stageDrag.id !== e.pointerId) return;
      var dx = e.clientX - stageDrag.startX;
      var moved = stageDrag.moved;
      stageDrag = null;
      els.rail.classList.remove('is-dragging');
      if (!moved) return;
      stageSwallow = true;
      var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
      var logical = rtl ? dx : -dx; // + = advance (next)
      var threshold = Math.min(60, els.rail.clientWidth * 0.15);
      if (logical > threshold) goTo(activeIndex() + 1);
      else if (logical < -threshold) goTo(activeIndex() - 1);
      else goTo(activeIndex());
    }
    els.rail.addEventListener('pointerup', endStageDrag);
    els.rail.addEventListener('pointercancel', endStageDrag);

    // reviews carousel: auto-advance every 3s + arrows + finger swipe.
    // Manual navigation resets the 3s clock; hover/focus/drag pause it (see
    // startRevAuto / reviewAutoTick). prefers-reduced-motion = no autoplay.
    els.revprev.addEventListener('click', function () {
      goToReview(state.review - 1);
      startRevAuto();
    });
    els.revnext.addEventListener('click', function () {
      goToReview(state.review + 1);
      startRevAuto();
    });
    // pause on hover / keyboard focus, resume on leave / blur
    els.revvp.addEventListener('mouseenter', function () {
      if (state) state.revPaused = true;
    });
    els.revvp.addEventListener('mouseleave', function () {
      if (state) state.revPaused = false;
    });
    els.revvp.addEventListener('focusin', function () {
      if (state) state.revPaused = true;
    });
    els.revvp.addEventListener('focusout', function () {
      if (state) state.revPaused = false;
    });
    var revDrag = null;
    els.revvp.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button')) return;
      revDrag = { x: e.clientX, id: e.pointerId, captured: false };
      if (state) state.revDragging = true;
      els.revtrack.style.transition = 'none';
    });
    els.revvp.addEventListener('pointermove', function (e) {
      if (!revDrag) return;
      var dx = e.clientX - revDrag.x;
      if (!revDrag.captured && Math.abs(dx) > 6) {
        try {
          els.revvp.setPointerCapture(revDrag.id);
        } catch (err) {
          /* noop */
        }
        revDrag.captured = true;
      }
      if (revDrag.captured) {
        var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
        var base = (rtl ? 1 : -1) * state.review * 100;
        els.revtrack.style.transform = 'translateX(calc(' + base + '% + ' + dx + 'px))';
      }
    });
    function endRevDrag(e) {
      if (!revDrag) return;
      var dx = e.clientX - revDrag.x;
      var wasDrag = revDrag.captured;
      revDrag = null;
      if (state) state.revDragging = false;
      els.revtrack.style.transition = '';
      startRevAuto(); // reset the 3s clock after an interaction
      if (!wasDrag) return;
      var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
      var logical = rtl ? dx : -dx; // + = advance
      var threshold = Math.min(60, els.revvp.clientWidth * 0.2);
      if (logical > threshold) goToReview(state.review + 1);
      else if (logical < -threshold) goToReview(state.review - 1);
      else goToReview(state.review);
    }
    els.revvp.addEventListener('pointerup', endRevDrag);
    els.revvp.addEventListener('pointercancel', endRevDrag);

    // qty stepper
    function qty() {
      var n = parseInt(els.qtyInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }
    function setQty(n) {
      n = Math.max(1, n);
      els.qtyInput.value = String(n);
      els.quantity.value = String(n);
    }
    els.qtyMinus.addEventListener('click', function () {
      setQty(qty() - 1);
    });
    els.qtyPlus.addEventListener('click', function () {
      setQty(qty() + 1);
    });
    els.qtyInput.addEventListener('change', function () {
      setQty(qty());
    });

    // size guide: open the page's popup when present, else product page anchor
    els.sizeguide.addEventListener('click', function () {
      var pop = document.getElementById('lx-size-guide');
      if (pop && typeof pop.showModal === 'function') {
        if (!pop.open) pop.showModal();
      } else if (state) {
        window.location.assign(state.model.url + '#lx-size-guide');
      }
    });

    // NOTIFY-ME (Build A): the sold-out state now opens the shared LxNotify
    // modal for THIS product + variant instead of deep-linking to the product
    // page. If the engine failed to load, we fall through to the anchor's href
    // (?variant=) deep link — the no-JS / failure fallback is preserved.
    els.notify.addEventListener('click', function (e) {
      if (window.LxNotify && state && state.variant) {
        e.preventDefault();
        window.LxNotify.open({
          productTitle: state.model.title,
          variantTitle: state.variant.title || state.variant.option1 || '',
          productId: state.model.data && state.model.data.id,
          variantId: state.variant.id
        });
      }
    });

    // NO PRE-SELECTED SIZE (recon item 2): with no size chosen the variant id
    // is empty; guard the submit in CAPTURE phase (before Dawn's product-form
    // form listener) so we never POST a wrong/empty variant. Instead we prompt
    // "select a size" and flash the pills. Products without options fall
    // straight through (their single variant is mounted).
    els.productform.addEventListener(
      'submit',
      function (e) {
        if (state && state.model.hasOptions && !state.variant) {
          e.preventDefault();
          e.stopPropagation();
          promptSelectSize();
        }
      },
      true
    );

    // ATC busy flag: lets the cartUpdate subscriber know WE triggered the add
    els.productform.addEventListener('submit', function () {
      if (state && !(state.model.hasOptions && !state.variant)) state.submitting = true;
    });

    // video overlay — backdrop close is drag-safe (down must land on the wash)
    els.playerClose.addEventListener('click', closePlayer);
    var playerDownWash = false;
    els.player.addEventListener('pointerdown', function (e) {
      playerDownWash = e.target === els.player;
    });
    els.player.addEventListener('click', function (e) {
      if (e.target === els.player && playerDownWash) closePlayer();
      playerDownWash = false;
    });

    // drag-to-close (mobile grip)
    var dragY = null;
    els.grip.addEventListener('pointerdown', function (e) {
      dragY = e.clientY;
      els.panel.style.transition = 'none';
      els.grip.setPointerCapture(e.pointerId);
    });
    els.grip.addEventListener('pointermove', function (e) {
      if (dragY == null) return;
      var dy = e.clientY - dragY;
      els.panel.style.transform = 'translateY(' + (dy > 0 ? dy : dy * 0.15) + 'px)';
    });
    function endDrag(e) {
      if (dragY == null) return;
      var dy = e.clientY - dragY;
      dragY = null;
      els.panel.style.transition = '';
      els.panel.style.transform = '';
      if (dy > 110) hide();
    }
    els.grip.addEventListener('pointerup', endDrag);
    els.grip.addEventListener('pointercancel', endDrag);

    // close ourselves once OUR add lands; the cart drawer takes the stage
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!event || event.source !== 'product-form') return;
        if (!dlg || !dlg.open || !state || !state.submitting) return;
        var belongs = (state.model.data.variants || []).some(function (v) {
          return String(v.id) === String(event.productVariantId);
        });
        if (!belongs) return;
        state.submitting = false;
        hide(true);
      });
    }
  }

  /* ======================================================================== */
  /* Mount a product model into the shell                                     */
  /* ======================================================================== */

  function mount(model) {
    ensureNotifyLoaded(); // have the back-in-stock modal ready before a sold-out pick
    state = {
      model: model,
      // NO PRE-SELECTED SIZE: products WITH options open with nothing chosen
      // (variant null); single-variant products still mount their only variant.
      variant: model.hasOptions ? null : model.initial,
      unit: null,
      active: 0,
      review: 0,
      revPaused: false,
      revDragging: false,
      revAuto: null,
      submitting: false,
      mediaCount: model.media.length,
      slides: [],
      dots: [],
      thumbs: [],
      revCards: [],
      revDots: [],
      lightbox: [] // image items (product photos + feed photos) for LxLightbox
    };

    // low-stock chip: never shown pre-selection — a fresh mount clears it
    els.lowstock.hidden = true;
    els.lowstock.classList.remove('is-in');

    // The <dialog> shell is built ONCE and reused across products, so reset the
    // buy UI to a clean "no size selected" state — otherwise a previous
    // product's disabled/notify/error state would leak in (variant is null now,
    // so applyVariant would no-op and never clear it).
    els.productform.hidden = false;
    els.notify.hidden = true;
    els.atc.removeAttribute('disabled');
    els.atc.removeAttribute('aria-disabled');
    els.atcText.textContent = (model.data.labels && model.data.labels.addToCart) || T.addToCart;
    els.id.value = state.variant ? String(state.variant.id) : '';
    if (els.error) els.error.hidden = true;
    els.sizes.classList.remove('is-flash');

    // header
    els.title.textContent = model.title;
    els.badge.innerHTML = model.badgeHtml;
    if (model.reviewCount) {
      els.rating.hidden = false;
      els.count.textContent = model.reviewCount;
    } else {
      els.rating.hidden = true;
    }

    // price snapshot from the fetched page (already localized, .money intact)
    els.price.innerHTML = model.priceHtml;
    els.compare.innerHTML = model.compareHtml;
    els.compare.setAttribute('data-hidden', model.compareHidden ? 'true' : 'false');
    if (model.fourpayMsgHtml) {
      els.fourpay.hidden = false;
      els.fourpayMsg.innerHTML = model.fourpayMsgHtml;
      els.fourpayIcons.innerHTML = model.fourpayIcons
        .map(function (icon) {
          return '<img src="' + esc(icon.src) + '" alt="' + esc(icon.alt) + '" loading="lazy">';
        })
        .join('');
    } else {
      els.fourpay.hidden = true;
    }

    buildGallery(model);
    buildSizes(model);
    buildReviews(model);

    // qty: visible stepper only for no-size products (socks path)
    els.qty.hidden = model.hasOptions;
    els.qtyInput.value = '1';
    els.quantity.value = '1';

    els.details.setAttribute('href', model.url);
    dlg.setAttribute('aria-label', (T.chooseOptionsFor || '[title]').replace('[title]', model.title));

    applyVariant(state.variant, true);
    reconvertCurrency();
  }

  /* ---- gallery ------------------------------------------------------------
     v2: ONE unified flow — product media first, then the FROM-THE-FEED items
     (feed image -> photo slide, feed video -> poster+play slide). Dots cover
     everything. Photo slides open LxLightbox (images only — videos are
     excluded from lightbox nav by design) and carry the magnifier hover chip.
     v4: the thumbnail rail ALSO spans the unified flow now — product-media
     thumbs then feed thumbs (a play badge on video ones), one wrapping grid of
     larger square thumbs. The separate FROM-THE-FEED strip is retired; every
     thumb (product or feed) drives the stage via goTo(unifiedIndex). */

  function setWidth(src, w) {
    if (!src) return '';
    if (/([?&])width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + w);
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function addPhotoSlide(slide, lbItem) {
    var lbIndex = state.lightbox.length;
    state.lightbox.push(lbItem);
    slide.setAttribute('data-lightbox-index', String(lbIndex));
    slide.addEventListener('click', function (e) {
      if (e.target.closest('button')) return;
      if (window.LxLightbox) window.LxLightbox.open(state.lightbox, lbIndex);
    });
    if (window.LxLightbox) window.LxLightbox.chip(slide);
  }

  function buildGallery(model) {
    var rail = els.rail;
    rail.innerHTML = '';
    els.dots.innerHTML = '';
    els.thumbs.innerHTML = '';
    state.slides = [];
    state.dots = [];
    state.thumbs = [];
    state.lightbox = [];

    var first = model.media[0];
    var ratio = 1;
    if (first && first.w > 0 && first.h > 0) ratio = first.w / first.h;
    ratio = Math.max(0.78, Math.min(1.35, ratio));
    // v3: --qs-ratio moved from the stage onto the MEDIA COLUMN. Custom props
    // inherit, so the rail's aspect-ratio still reads it — but now the thumb
    // rail (a SIBLING of the stage) can read it too, which is what lets both
    // share the --qs-frame width and stay edge-aligned.
    els.mediacol.style.setProperty('--qs-ratio', String(ratio));

    model.media.forEach(function (m, i) {
      var slide = document.createElement('div');
      slide.className = 'lx-qs__slide';
      slide.setAttribute('data-index', String(i));
      if (m.type === 'video') {
        var video = document.createElement('video');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'none');
        if (m.poster) video.setAttribute('poster', m.poster);
        video.dataset.src = m.src;
        slide.appendChild(video);
        var play = document.createElement('button');
        play.type = 'button';
        play.className = 'lx-qs__slide-play';
        play.setAttribute('aria-label', T.playVideo || 'Play video');
        play.innerHTML = SVG.play;
        play.addEventListener('click', function () {
          if (!video.getAttribute('src') && video.dataset.src) video.setAttribute('src', video.dataset.src);
          video.setAttribute('controls', '');
          play.style.display = 'none';
          video.play();
        });
        slide.appendChild(play);
      } else if (m.type === 'embed') {
        var eimg = document.createElement('img');
        if (m.poster) eimg.src = m.poster;
        eimg.alt = m.alt || '';
        slide.appendChild(eimg);
      } else {
        var img = document.createElement('img');
        img.src = m.src;
        if (m.srcset) img.setAttribute('srcset', m.srcset);
        img.setAttribute('sizes', '(max-width: 1024px) 100vw, 560px');
        img.alt = m.alt || '';
        if (i > 0) img.loading = 'lazy';
        img.decoding = 'async';
        slide.appendChild(img);
        addPhotoSlide(slide, { src: setWidth(m.src, 1600), srcset: m.srcset || '', alt: m.alt || '' });
      }
      rail.appendChild(slide);
      state.slides.push(slide);

      // thumb (product media) — feed thumbs are appended to this same rail below
      var thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'lx-qs__thumb';
      thumb.setAttribute('data-index', String(i));
      thumb.setAttribute('aria-label', (T.slideOf || 'Slide') + ' ' + (i + 1));
      var timg = document.createElement('img');
      timg.src = thumbSrc(m);
      timg.alt = '';
      timg.loading = 'lazy';
      thumb.appendChild(timg);
      if (m.type === 'video' || m.type === 'embed') {
        var badge = document.createElement('span');
        badge.className = 'lx-qs__thumb-play';
        badge.innerHTML = SVG.play;
        thumb.appendChild(badge);
      }
      thumb.addEventListener('click', function () {
        goTo(i);
      });
      els.thumbs.appendChild(thumb);
      state.thumbs.push(thumb);
    });

    // FEED items continue the main flow after the product media
    model.feed.forEach(function (item, j) {
      var slide = document.createElement('div');
      slide.className = 'lx-qs__slide lx-qs__slide--feed';
      slide.setAttribute('data-index', String(state.mediaCount + j));
      if (item.kind === 'video') {
        var poster = document.createElement('img');
        poster.src = setWidth(item.poster || item.image, 1024);
        poster.alt = '';
        poster.loading = 'lazy';
        poster.decoding = 'async';
        slide.appendChild(poster);
        var fplay = document.createElement('button');
        fplay.type = 'button';
        fplay.className = 'lx-qs__slide-play';
        fplay.setAttribute('aria-label', T.playVideo || 'Play video');
        fplay.innerHTML = SVG.play;
        fplay.addEventListener('click', function () {
          openPlayer(item.video);
        });
        slide.appendChild(fplay);
      } else {
        var fimg = document.createElement('img');
        fimg.src = setWidth(item.image || item.poster, 1024);
        fimg.alt = '';
        fimg.loading = 'lazy';
        fimg.decoding = 'async';
        slide.appendChild(fimg);
        addPhotoSlide(slide, { src: setWidth(item.image || item.poster, 1600), srcset: '', alt: '' });
      }
      rail.appendChild(slide);
      state.slides.push(slide);

      // v4: feed thumb continues the SAME rail, index = unified stage index
      var feedIndex = state.mediaCount + j;
      var fthumb = document.createElement('button');
      fthumb.type = 'button';
      fthumb.className = 'lx-qs__thumb lx-qs__thumb--feed';
      fthumb.setAttribute('data-index', String(feedIndex));
      fthumb.setAttribute('aria-label', (T.slideOf || 'Slide') + ' ' + (feedIndex + 1));
      var ftimg = document.createElement('img');
      ftimg.src = setWidth(item.kind === 'video' ? item.poster || item.image : item.image || item.poster, 240);
      ftimg.alt = '';
      ftimg.loading = 'lazy';
      fthumb.appendChild(ftimg);
      if (item.kind === 'video') {
        var fbadge = document.createElement('span');
        fbadge.className = 'lx-qs__thumb-play';
        fbadge.innerHTML = SVG.play;
        fthumb.appendChild(fbadge);
      }
      fthumb.addEventListener('click', function () {
        goTo(feedIndex);
        // video thumb -> plays as today (jump to the poster+play slide); on
        // mobile bring the stage into view like the old feed strip did
        if (window.matchMedia('(max-width: 1024px)').matches) {
          els.scroll.scrollTo({ top: 0, behavior: REDUCED.matches ? 'auto' : 'smooth' });
        }
      });
      els.thumbs.appendChild(fthumb);
      state.thumbs.push(fthumb);
    });

    buildDots();
    rail.scrollTo({ left: 0 });
    setActive(0);
  }

  function thumbSrc(m) {
    var src = m.type === 'image' ? m.src : m.poster;
    if (!src) return '';
    // v4: larger square thumbs (76px @ up to 3x) -> request 240px wide
    return setWidth(src, 240);
  }

  function buildDots() {
    els.dots.innerHTML = '';
    state.dots = [];
    state.slides.forEach(function (_, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lx-qs__dot';
      dot.setAttribute('aria-label', (T.slideOf || 'Slide') + ' ' + (i + 1));
      dot.addEventListener('click', function () {
        goTo(i);
      });
      els.dots.appendChild(dot);
      state.dots.push(dot);
    });
    els.dots.style.display = state.slides.length > 1 ? '' : 'none';
  }

  function activeIndex() {
    return state ? state.active : 0;
  }

  function goTo(i) {
    if (!state) return;
    var n = state.slides.length;
    if (!n) return;
    // LOOP: wrap past either end (no dead-ends). A wrap spans the whole rail,
    // so jump it instantly instead of a long smooth rewind across the thumbs.
    var wrapped = i < 0 || i >= n;
    i = ((i % n) + n) % n;
    var slide = state.slides[i];
    if (!slide) return;
    var r1 = els.rail.getBoundingClientRect();
    var r2 = slide.getBoundingClientRect();
    var delta = r2.left + r2.width / 2 - (r1.left + r1.width / 2);
    els.rail.scrollBy({ left: delta, behavior: REDUCED.matches || wrapped ? 'auto' : 'smooth' });
    setActive(i);
  }

  function syncActive() {
    if (!state) return;
    var r1 = els.rail.getBoundingClientRect();
    var center = r1.left + r1.width / 2;
    var best = 0;
    var bestDist = Infinity;
    state.slides.forEach(function (slide, i) {
      var r2 = slide.getBoundingClientRect();
      var d = Math.abs(r2.left + r2.width / 2 - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    if (best !== state.active) setActive(best);
  }

  function setActive(i) {
    state.active = i;
    state.dots.forEach(function (dot, j) {
      dot.setAttribute('data-active', String(j === i));
    });
    state.thumbs.forEach(function (thumb, j) {
      thumb.setAttribute('data-active', String(j === i));
    });
    // pause media on non-active slides
    state.slides.forEach(function (slide, j) {
      if (j !== i) {
        slide.querySelectorAll('video').forEach(function (v) {
          v.pause();
        });
      }
    });
    // LOOP: arrows never dead-end — only disabled when there's nothing to loop.
    els.prev.toggleAttribute('disabled', state.slides.length <= 1);
    els.next.toggleAttribute('disabled', state.slides.length <= 1);
  }

  /* ---- inline overlay player ---------------------------------------------- */

  function openPlayer(src) {
    if (!src) return;
    els.playerVideo.setAttribute('src', src);
    els.player.setAttribute('data-open', 'true');
    var p = els.playerVideo.play();
    if (p && p.catch) p.catch(function () {});
  }

  function closePlayer() {
    els.playerVideo.pause();
    els.player.removeAttribute('data-open');
    els.playerVideo.removeAttribute('src');
    try {
      els.playerVideo.load();
    } catch (e) {
      /* noop */
    }
  }

  /* ---- sizes + EU/US/UK units --------------------------------------------- */

  function parseUnits(value) {
    var parts = {};
    var regex = /(EU|US|UK)\s+(\d+(?:\.\d+)?)/g;
    var match;
    while ((match = regex.exec(value)) !== null) parts[match[1]] = match[2];
    return parts;
  }

  function unitLabel(parts, unit, full) {
    if (!unit || !parts[unit]) return full;
    return unit === 'EU' ? parts[unit] : parts[unit] + ' ' + unit;
  }

  function buildSizes(model) {
    els.pills.innerHTML = '';
    els.units.innerHTML = '';
    if (!model.hasOptions) {
      els.sizes.hidden = true;
      return;
    }
    els.sizes.hidden = false;

    var variants = model.data.variants || [];
    var unitSet = {};
    variants.forEach(function (v) {
      var parts = parseUnits(v.option1 || '');
      Object.keys(parts).forEach(function (u) {
        unitSet[u] = true;
      });
    });
    var units = ['EU', 'US', 'UK'].filter(function (u) {
      return unitSet[u];
    });
    state.unit = units.indexOf('EU') !== -1 ? 'EU' : units[0] || null;

    if (units.length > 1) {
      els.units.hidden = false;
      units.forEach(function (u) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lx-qs__unit';
        btn.textContent = u;
        btn.setAttribute('data-unit', u);
        btn.setAttribute('data-active', String(u === state.unit));
        btn.addEventListener('click', function () {
          state.unit = u;
          els.units.querySelectorAll('.lx-qs__unit').forEach(function (b) {
            b.setAttribute('data-active', String(b === btn));
          });
          relabelPills();
        });
        els.units.appendChild(btn);
      });
    } else {
      els.units.hidden = true;
    }

    var groupName = 'lxqs-' + model.data.id + '-option';
    variants.forEach(function (v) {
      var label = document.createElement('label');
      label.className = 'lx-qs-pill';
      // parity note: sold-out pills render EXACTLY like the product page's —
      // no strike/gray, still clickable. The attribute stays for hooks/tests.
      label.setAttribute('data-soldout', String(!v.available));
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = groupName;
      input.value = v.option1 || '';
      if (state.variant && v.id === state.variant.id) input.checked = true;
      input.addEventListener('change', function () {
        var match = variants.filter(function (x) {
          return x.option1 === input.value;
        })[0];
        if (match) applyVariant(match);
        showLowStock(); // keyboard path (arrow keys fire change without click)
      });
      var box = document.createElement('span');
      box.className = 'lx-qs-pill__box';
      var txt = document.createElement('span');
      txt.setAttribute('data-full-value', v.option1 || '');
      txt.textContent = v.option1 || '';
      box.appendChild(txt);
      label.title = v.option1 || '';
      label.appendChild(input);
      label.appendChild(box);
      // low-stock urgency chip (now ABOVE the size head): EVERY size, EVERY
      // click — including re-clicks of the already-selected pill (owner call;
      // change alone won't re-fire)
      label.addEventListener('click', function () {
        showLowStock();
      });
      els.pills.appendChild(label);
    });
    relabelPills();
  }

  /* ---- low-stock urgency chip (owner-directed marketing) -------------------
     Hidden until the shopper clicks a size; soft 200ms fade/rise; re-triggers
     its animation on every size interaction. Cleared on every mount().
     v3: it renders ABOVE the size head now (product-page parity) — a pure DOM
     move, so this trigger is byte-for-byte the one that shipped. */
  function showLowStock() {
    var el = els.lowstock;
    if (!el || els.sizes.hidden) return;
    el.hidden = false;
    el.classList.remove('is-in');
    void el.offsetWidth; // restart the animation
    el.classList.add('is-in');
  }

  /* ---- "select a size" prompt (recon item 2) ------------------------------
     Shown when ATC is pressed with no size chosen. Reuses the product-form
     error slot for the message and gives the pills a short attention flash. */
  function promptSelectSize() {
    if (els.error) {
      var msg = els.error.querySelector('.product-form__error-message') || els.error;
      msg.textContent = T.selectSize || 'Please select a size';
      els.error.hidden = false;
    }
    if (els.sizes && !els.sizes.hidden) {
      els.sizes.classList.remove('is-flash');
      void els.sizes.offsetWidth; // restart the flash
      els.sizes.classList.add('is-flash');
      try {
        els.sizes.scrollIntoView({ block: 'nearest', behavior: REDUCED.matches ? 'auto' : 'smooth' });
      } catch (e) {
        /* noop */
      }
    }
  }

  function relabelPills() {
    els.pills.querySelectorAll('[data-full-value]').forEach(function (txt) {
      var full = txt.getAttribute('data-full-value') || '';
      txt.textContent = unitLabel(parseUnits(full), state.unit, full);
    });
  }

  /* ---- variant state -------------------------------------------------------- */

  function applyVariant(variant, initial) {
    if (!variant) return;
    state.variant = variant;
    els.id.value = String(variant.id);

    // a real selection clears the "select a size" prompt + flash
    if (!initial) {
      if (els.error) els.error.hidden = true;
      els.sizes.classList.remove('is-flash');
    }

    var format = state.model.data.moneyFormat;
    if (format) {
      if (!initial || !state.model.priceHtml) els.price.innerHTML = formatMoney(variant.price, format);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        els.compare.innerHTML = formatMoney(variant.compare_at_price, format);
        els.compare.setAttribute('data-hidden', 'false');
      } else if (!initial) {
        els.compare.setAttribute('data-hidden', 'true');
      }
      var quarter = els.fourpayMsg.querySelector('[data-lx-quarter]');
      if (quarter && !initial) quarter.innerHTML = formatMoney(Math.floor(variant.price / 4), format);
    }

    var labels = state.model.data.labels || {};
    if (variant.available) {
      els.productform.hidden = false;
      els.notify.hidden = true;
      els.atc.removeAttribute('disabled');
      els.atc.removeAttribute('aria-disabled');
      els.atcText.textContent = labels.addToCart || T.addToCart;
    } else {
      // NOTIFY-ME path: Peppy's trigger is product-page-only (its form module
      // never loads elsewhere), so the ATC swaps for a locale-aware deep link
      // that preselects this variant where the app widget lives.
      els.atc.setAttribute('disabled', 'disabled');
      els.atc.setAttribute('aria-disabled', 'true');
      els.atcText.textContent = labels.outOfStock || T.outOfStock;
      els.productform.hidden = true;
      els.notify.hidden = false;
      els.notify.setAttribute('href', state.model.url + '?variant=' + variant.id);
    }

    // variant featured media swap (when the variant JSON carries one)
    var mediaId = variant.featured_media_id || (variant.featured_media && variant.featured_media.id);
    if (mediaId && !initial) {
      var pos = variant.featured_media && variant.featured_media.position;
      if (pos) goTo(pos - 1);
    }

    if (!initial) reconvertCurrency();
  }

  /* ---- reviews ---------------------------------------------------------------- */

  function firstName(author) {
    var name = (author || '').trim();
    if (!name) return '';
    var parts = name.split(/\s+/);
    return parts[0];
  }

  /* v2: compact single-card carousel — height-stable card, 6px dots (active
     ink), subtle hover arrows, swipe. Manual only, max 4 (sliced in parse). */
  function buildReviews(model) {
    els.revtrack.innerHTML = '';
    els.revdots.innerHTML = '';
    state.revCards = [];
    state.revDots = [];
    state.review = 0;
    if (!model.reviews.length) {
      els.reviews.hidden = true;
      return;
    }
    els.reviews.hidden = false;
    model.reviews.forEach(function (r, i) {
      var card = document.createElement('article');
      card.className = 'lx-qs__review';
      var top = document.createElement('div');
      top.className = 'lx-qs__review-top';
      // v5 order: avatar chip (reviewer's first initial) -> name -> stars
      // (CSS anchors the stars inline-end; logical margin mirrors on /ar)
      var initial = firstName(r.author).charAt(0);
      if (initial) {
        var avatar = document.createElement('span');
        avatar.className = 'lx-qs__review-avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = initial;
        top.appendChild(avatar);
      }
      var name = document.createElement('span');
      name.className = 'lx-qs__review-name';
      name.textContent = firstName(r.author);
      top.appendChild(name);
      var st = document.createElement('span');
      st.className = 'lx-qs__review-stars';
      st.setAttribute('aria-hidden', 'true');
      st.innerHTML = stars(r.score);
      top.appendChild(st);
      card.appendChild(top);
      var text = document.createElement('p');
      text.className = 'lx-qs__review-text';
      text.textContent = r.text;
      card.appendChild(text);
      els.revtrack.appendChild(card);
      state.revCards.push(card);

      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lx-qs__rev-dot';
      dot.setAttribute('aria-label', (T.slideOf || 'Slide') + ' ' + (i + 1));
      dot.addEventListener('click', function () {
        goToReview(i);
        startRevAuto(); // reset the 3s clock after a manual jump
      });
      els.revdots.appendChild(dot);
      state.revDots.push(dot);
    });
    var single = model.reviews.length <= 1;
    els.revdots.hidden = single;
    els.revprev.hidden = single;
    els.revnext.hidden = single;
    goToReview(0);
  }

  function goToReview(i) {
    if (!state) return;
    i = Math.max(0, Math.min(state.revCards.length - 1, i));
    state.review = i;
    var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    els.revtrack.style.transform = 'translateX(' + (rtl ? 1 : -1) * i * 100 + '%)';
    state.revDots.forEach(function (dot, j) {
      dot.setAttribute('data-active', String(j === i));
    });
    els.revprev.toggleAttribute('disabled', i <= 0);
    els.revnext.toggleAttribute('disabled', i >= state.revCards.length - 1);
  }

  /* ---- reviews autoplay (recon item 3) ------------------------------------
     Auto-advance every 3s, looping. Paused on hover/focus (revPaused) and
     while dragging (revDragging); manual dots/arrows reset the clock via
     startRevAuto(). prefers-reduced-motion disables it entirely. */
  function reviewAutoTick() {
    if (!dlg || !dlg.open || !state) return;
    if (REDUCED.matches) return;
    if (state.revPaused || state.revDragging) return;
    if (state.revCards.length <= 1) return;
    goToReview((state.review + 1) % state.revCards.length);
  }

  function startRevAuto() {
    stopRevAuto();
    if (REDUCED.matches) return;
    if (!state || state.revCards.length <= 1) return;
    state.revAuto = setInterval(reviewAutoTick, 3000);
  }

  function stopRevAuto() {
    if (state && state.revAuto) {
      clearInterval(state.revAuto);
      state.revAuto = null;
    }
  }

  /* ======================================================================== */
  /* Open / close                                                             */
  /* ======================================================================== */

  var closing = false;

  function show(opener) {
    state.opener = opener || null;
    closePlayer();
    document.body.classList.add('overflow-hidden');
    if (!dlg.open) dlg.showModal();
    // reset scroll positions for a fresh product
    els.scroll.scrollTop = 0;
    els.mediacol.scrollTop = 0;
    els.infocol.scrollTop = 0;
    els.rail.scrollTo({ left: 0 });
    setActive(0);
    if (REDUCED.matches) {
      dlg.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          dlg.classList.add('is-open');
        });
      });
    }
    try {
      els.panel.focus({ preventScroll: true });
    } catch (e) {
      els.panel.focus();
    }
    startRevAuto(); // begin reviews autoplay once the dialog is open
  }

  function hide(fast) {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    stopRevAuto();
    closePlayer();
    if (state) {
      state.slides.forEach(function (slide) {
        slide.querySelectorAll('video').forEach(function (v) {
          v.pause();
        });
      });
    }
    dlg.classList.remove('is-open');
    dlg.classList.add('is-closing');
    var opener = state && state.opener;
    var done = function () {
      dlg.classList.remove('is-closing');
      closing = false;
      if (dlg.open) dlg.close();
      document.body.classList.remove('overflow-hidden');
      if (opener && document.contains(opener)) {
        try {
          opener.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
    };
    if (REDUCED.matches || fast === true) {
      // fast path (ATC success): get out of the drawer's way immediately
      done();
    } else {
      setTimeout(done, 300);
    }
  }

  /* ======================================================================== */
  /* Opener interception                                                      */
  /* ======================================================================== */

  function busy(btn, on) {
    if (!btn) return;
    var spinner = btn.querySelector('.loading__spinner');
    if (on) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  function openFor(btn) {
    var raw = btn.getAttribute('data-lx-quickshop') || btn.getAttribute('data-product-url') || '';
    var url = localizeUrl(raw);
    var handle = btn.getAttribute('data-lx-quickshop-handle') || url.split('/').pop();
    if (!url) return;
    busy(btn, true);
    load(handle, url)
      .then(function (model) {
        ensureShell();
        mount(model);
        show(btn);
      })
      .catch(function (err) {
        console.warn('[lx-quickshop] falling back to product page:', err);
        window.location.assign(url);
      })
      .then(function () {
        busy(btn, false);
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-lx-quickshop]') : null;
      if (!btn) return;
      if (btn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // capture phase: runs before Dawn's modal-opener button listener
      e.preventDefault();
      e.stopPropagation();
      openFor(btn);
    },
    true
  );

  CFG = readConfig();
  T = CFG.i18n;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      CFG = readConfig();
      T = CFG.i18n;
    });
  }
})();
